import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatCard } from "./StatCard";
import { BranchComparisonChart } from "./BranchComparisonChart";
import { MonthlySnapshot } from "@/hooks/useMonthlySnapshot";
import { 
  Clock, 
  Users, 
  GraduationCap, 
  Wallet, 
  AlertTriangle, 
  FileWarning,
  BookX,
  UserMinus
} from "lucide-react";

interface MonthlySnapshotDashboardProps {
  current: MonthlySnapshot;
  percentChanges: Record<string, number | null>;
}

export function MonthlySnapshotDashboard({ current, percentChanges }: MonthlySnapshotDashboardProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const latePercent = current.total_reports > 0 
    ? ((current.submitted_late / current.total_reports) * 100).toFixed(2)
    : "0";

  const totalSalary = current.total_teacher_salary + current.total_teacher_commission + current.total_trial_costs;

  return (
    <div className="space-y-6">
      {/* Overview Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">📊</span> ภาพรวม
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="ชั่วโมงคงเหลือทั้งหมด"
            value={current.total_hours.toLocaleString("th-TH")}
            suffix="ชม."
            percentChange={percentChanges.total_hours}
            icon={<Clock className="h-5 w-5" />}
          />
          <StatCard
            title="ชั่วโมง Active"
            value={current.active_hours.toLocaleString("th-TH")}
            suffix="ชม."
            percentChange={percentChanges.active_hours}
            icon={<Clock className="h-5 w-5" />}
          />
          <StatCard
            title="นักเรียน Active"
            value={current.active_student_count}
            suffix="คน"
            percentChange={percentChanges.active_student_count}
            icon={<Users className="h-5 w-5" />}
          />
          <StatCard
            title="ชั่วโมงสอนทั้งหมด"
            value={current.total_teaching_hours.toLocaleString("th-TH")}
            suffix="ชม."
            percentChange={percentChanges.total_teaching_hours}
            icon={<GraduationCap className="h-5 w-5" />}
          />
        </div>
      </div>

      {/* Salary Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">💰</span> เงินเดือนครู
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="เงินเดือนครูรวม"
            value={formatCurrency(totalSalary)}
            suffix="฿"
            percentChange={percentChanges.total_teacher_salary}
            icon={<Wallet className="h-5 w-5" />}
          />
          <StatCard
            title="เงินเดือนหลัก"
            value={formatCurrency(current.total_teacher_salary)}
            suffix="฿"
            percentChange={percentChanges.total_teacher_salary}
          />
          <StatCard
            title="ค่าคอมมิชชั่น"
            value={formatCurrency(current.total_teacher_commission)}
            suffix="฿"
            percentChange={percentChanges.total_teacher_commission}
          />
          <StatCard
            title="ค่าทดลองเรียน"
            value={formatCurrency(current.total_trial_costs)}
            suffix="฿"
            percentChange={percentChanges.total_trial_costs}
          />
        </div>
      </div>

      {/* Issues Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">⚠️</span> ปัญหา
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            title="ลากระทันหัน"
            value={current.sudden_leave_hours}
            suffix="ชม."
            percentChange={percentChanges.sudden_leave_hours}
            inverseColors={true}
            icon={<AlertTriangle className="h-5 w-5" />}
          />
          <StatCard
            title="เงินจ่ายลากระทันหัน"
            value={formatCurrency(current.sudden_leave_paid)}
            suffix="฿"
            percentChange={percentChanges.sudden_leave_paid}
            inverseColors={true}
            icon={<Wallet className="h-5 w-5" />}
          />
          <StatCard
            title="ส่งพัฒนาการช้า"
            value={`${latePercent}%`}
            suffix={`(${current.submitted_late}/${current.total_reports})`}
            percentChange={percentChanges.late_percent}
            inverseColors={true}
            icon={<FileWarning className="h-5 w-5" />}
          />
        </div>
      </div>

      {/* Branch Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">🏢</span> แยกตามสาขา
        </h3>
        <BranchComparisonChart
          online={{ hours: current.teaching_hours_online, salary: current.salary_online }}
          siam={{ hours: current.teaching_hours_siam, salary: current.salary_siam }}
          ngamwongwan={{ hours: current.teaching_hours_ngamwongwan, salary: current.salary_ngamwongwan }}
        />
      </div>

      {/* Course Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">📚</span> คอร์ส
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            title="เด็กหมดคอร์ส"
            value={current.course_ended_student_count}
            suffix="คน"
            percentChange={percentChanges.course_ended_student_count}
            icon={<BookX className="h-5 w-5" />}
          />
          <StatCard
            title="ไม่ต่อคอร์ส"
            value={current.not_continue_student_count}
            suffix="คน"
            percentChange={percentChanges.not_continue_student_count}
            inverseColors={true}
            icon={<UserMinus className="h-5 w-5" />}
          />
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Retention Rate</p>
              <p className="text-2xl font-bold mt-1">
                {current.course_ended_student_count > 0
                  ? (((current.course_ended_student_count - current.not_continue_student_count) / current.course_ended_student_count) * 100).toFixed(1)
                  : "N/A"}
                <span className="text-base font-normal ml-1">%</span>
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                ต่อคอร์ส: {current.course_ended_student_count - current.not_continue_student_count} คน
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
